import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { formatCurrency, formatCurrencyInput, processCurrencyInput, formatCurrencyOnBlur } from '../../utils/formatters';
import ExpenseSummaryCard from './ExpenseSummaryCard';
import Modal from '../shared/Modal';
import logger from '../../utils/logger';
import api from '../../utils/apiConfig';
import { CARD_BASE } from '../../constants/styles';

const ExpenseAnalysisTable = ({
    expenses = [],
    members = [],
    onAddExpense,
    onEditExpense,
    onDeleteExpense,
    isEditMode = false,
    onSave,
    onCancel,
}) => {
    const { isDarkMode } = useTheme();
    const [showEditPanel, setShowEditPanel] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [expenseTypes, setExpenseTypes] = useState({});
    const [frequencies, setFrequencies] = useState([]);

    // Fetch reference data
    useEffect(() => {
        const fetchReferenceData = async () => {
            try {
                logger.info("Fetching reference data...");
                const response = await api.get("/advisor-portal/reference-data");
                if (response.data?.expenseTypes) {
                    const typesMap = response.data.expenseTypes.reduce((acc, type) => {
                        acc[type.expense_type_id] = {
                            expense_type_name: type.expense_type_name,
                            category: type.category,
                            behavior: type.behavior,
                        };
                        return acc;
                    }, {});
                    setExpenseTypes(typesMap);
                }
                if (response.data?.frequencies) {
                    setFrequencies(response.data.frequencies);
                } else {
                    setFrequencies([
                        { frequency_id: 1, frequency_name: "Annual", frequency_multiplier: 1 },
                        { frequency_id: 2, frequency_name: "Monthly", frequency_multiplier: 12 },
                        { frequency_id: 3, frequency_name: "Bi-Weekly", frequency_multiplier: 26 },
                        { frequency_id: 4, frequency_name: "Quarterly", frequency_multiplier: 4 },
                        { frequency_id: 5, frequency_name: "Semi-Annual", frequency_multiplier: 2 },
                        { frequency_id: 6, frequency_name: "Weekly", frequency_multiplier: 52 },
                        { frequency_id: 7, frequency_name: "One-Time", frequency_multiplier: 1 },
                    ]);
                }
            } catch (error) {
                logger.error("Error fetching reference data:", error);
            }
        };
        fetchReferenceData();
    }, []);

    // Process expenses data
    const processedExpenses = useMemo(() => {
        return expenses.map((expense) => {
            const member = members.find((m) => m.id === expense.user_id);
            return {
                id: expense.client_expense_id,
                expense_id: expense.expense_id,
                user_id: expense.user_id,
                expense_type_id: expense.expense_type_id,
                expense_type_name: expense.expense_type_name,
                amount: expense.amount,
                frequency_id: expense.frequency_id,
                frequency_name: expense.frequency_name,
                frequency_multiplier: expense.frequency_multiplier,
                start_date: expense.start_date,
                end_date: expense.end_date,
                notes: expense.notes,
                category: expense.category_id,
                behavior: expense.behavior_id,
                memberName: member ? member.name : "Household",
                isHousehold: !expense.user_id,
                memberId: member?.id || 'household'
            };
        });
    }, [expenses, members]);

    const handleEditMode = () => setShowEditPanel(true);
    const handleSave = () => {
        onSave?.();
        setShowEditPanel(false);
    };
    const handleCancel = () => {
        onCancel?.();
        setShowEditPanel(false);
    };

    // Card hover/keyboard accessibility
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            handleEditMode();
        }
    };

    return (
        <div className="w-full relative">
            {/* Clickable Summary Card with accent line */}
            <div
                className={`${CARD_BASE} group flex h-full relative cursor-pointer hover:shadow-lg transition-shadow outline-none focus:ring-2 focus:ring-red-600`}
                tabIndex={0}
                aria-label="Edit expense details"
                onClick={handleEditMode}
                onKeyDown={handleKeyDown}
                role="button"
            >
                {/* Red accent line (left) */}
                <div className="absolute left-0 top-0 h-full w-1 rounded-l-lg bg-red-600 group-hover:bg-red-700 transition-colors"></div>
                <div className="flex-1">
                    <ExpenseSummaryCard
                        expenses={processedExpenses}
                        members={members}
                        expenseTypes={expenseTypes}
                        frequencies={frequencies}
                    />
                </div>
            </div>

            {/* Edit Modal */}
            <Modal
                isOpen={showEditPanel}
                onClose={handleCancel}
                maxWidth="4xl"
            >
                <div className="p-4">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-base font-semibold">Edit Expenses</h3>
                        <div className="flex gap-2">
                            <button
                                onClick={handleCancel}
                                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${isDarkMode
                                        ? 'text-gray-300 hover:bg-neutral-700'
                                        : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md 
                         hover:bg-blue-700 transition-colors focus:outline-none 
                         focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 
                         dark:focus:ring-offset-neutral-800"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b dark:border-neutral-700">
                                    <th className="text-left py-2 font-medium">Member</th>
                                    <th className="text-left py-2 font-medium">Type</th>
                                    <th className="text-right py-2 font-medium">Amount</th>
                                    <th className="text-right py-2 font-medium">Frequency</th>
                                    <th className="text-right py-2 font-medium">Annual</th>
                                    <th className="text-right py-2 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {processedExpenses
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((expense) => {
                                        const annualAmount =
                                            parseFloat(expense.amount || 0) *
                                            (expense.frequency_multiplier || 1);

                                        return (
                                            <tr
                                                key={expense.id}
                                                className={`border-b last:border-0 dark:border-neutral-700 
                                  ${isDarkMode ? 'hover:bg-neutral-700/50' : 'hover:bg-gray-50'}`}
                                            >
                                                <td className="py-2">
                                                    <select
                                                        value={expense.user_id || "household"}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            const updatedExpense = {
                                                                ...expense,
                                                                user_id: value === "household" ? null : value,
                                                                memberName: value === "household"
                                                                    ? "Household"
                                                                    : members.find((m) => m.id === value)?.name,
                                                            };
                                                            onEditExpense(updatedExpense);
                                                        }}
                                                        className={`w-full rounded border px-2 py-1 ${isDarkMode
                                                                ? 'bg-neutral-800 border-neutral-700'
                                                                : 'bg-white border-gray-300'
                                                            }`}
                                                    >
                                                        <option value="household">Household</option>
                                                        {members.map((member) => (
                                                            <option key={member.id} value={member.id}>
                                                                {member.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="py-2">
                                                    <select
                                                        value={expense.expense_type_id || ""}
                                                        onChange={(e) => {
                                                            const selectedType = expenseTypes[e.target.value];
                                                            const updatedExpense = {
                                                                ...expense,
                                                                expense_type_id: e.target.value,
                                                                expense_type_name: selectedType?.expense_type_name,
                                                            };
                                                            onEditExpense(updatedExpense);
                                                        }}
                                                        className={`w-full rounded border px-2 py-1 ${isDarkMode
                                                                ? 'bg-neutral-800 border-neutral-700'
                                                                : 'bg-white border-gray-300'
                                                            }`}
                                                    >
                                                        <option value="">Select Type</option>
                                                        {Object.entries(expenseTypes).map(([id, type]) => (
                                                            <option key={id} value={id}>
                                                                {type.expense_type_name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="py-2">
                                                    <input
                                                        type="text"
                                                        value={formatCurrencyInput(expense.amount || "")}
                                                        onChange={(e) => {
                                                            const updatedExpense = {
                                                                ...expense,
                                                                amount: processCurrencyInput(e.target.value),
                                                            };
                                                            onEditExpense(updatedExpense);
                                                        }}
                                                        onBlur={(e) => {
                                                            const updatedExpense = {
                                                                ...expense,
                                                                amount: formatCurrencyOnBlur(e.target.value),
                                                            };
                                                            onEditExpense(updatedExpense);
                                                        }}
                                                        className={`w-full text-right rounded border px-2 py-1 ${isDarkMode
                                                                ? 'bg-neutral-800 border-neutral-700'
                                                                : 'bg-white border-gray-300'
                                                            }`}
                                                    />
                                                </td>
                                                <td className="py-2">
                                                    <select
                                                        value={expense.frequency_id || ""}
                                                        onChange={(e) => {
                                                            const selectedFreq = frequencies.find(
                                                                (f) =>
                                                                    f.frequency_id === parseInt(e.target.value)
                                                            );
                                                            const updatedExpense = {
                                                                ...expense,
                                                                frequency_id: parseInt(e.target.value),
                                                                frequency_name: selectedFreq?.frequency_name,
                                                                frequency_multiplier:
                                                                    selectedFreq?.frequency_multiplier,
                                                            };
                                                            onEditExpense(updatedExpense);
                                                        }}
                                                        className={`w-full rounded border px-2 py-1 ${isDarkMode
                                                                ? 'bg-neutral-800 border-neutral-700'
                                                                : 'bg-white border-gray-300'
                                                            }`}
                                                    >
                                                        <option value="">Select Frequency</option>
                                                        {frequencies.map((freq) => (
                                                            <option
                                                                key={freq.frequency_id}
                                                                value={freq.frequency_id}
                                                            >
                                                                {freq.frequency_name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="py-2 text-right">
                                                    {formatCurrency(annualAmount)}
                                                </td>
                                                <td className="py-2">
                                                    <div className="flex justify-end gap-1">
                                                        <button
                                                            onClick={() => onDeleteExpense(expense)}
                                                            className="p-1 text-red-500 hover:text-red-600 
                                       transition-colors rounded-full 
                                       hover:bg-red-500/10"
                                                        >
                                                            <svg
                                                                className="w-4 h-4"
                                                                viewBox="0 0 20 20"
                                                                fill="currentColor"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="mt-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <select
                                value={rowsPerPage}
                                onChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value));
                                    setPage(0);
                                }}
                                className={`rounded border px-2 py-1 text-sm ${isDarkMode
                                        ? 'bg-neutral-800 border-neutral-700'
                                        : 'bg-white border-gray-300'
                                    }`}
                            >
                                <option value={5}>5 per page</option>
                                <option value={10}>10 per page</option>
                                <option value={25}>25 per page</option>
                                <option value={50}>50 per page</option>
                            </select>
                            <span className="text-sm opacity-75">
                                {page * rowsPerPage + 1}-
                                {Math.min(
                                    (page + 1) * rowsPerPage,
                                    processedExpenses.length
                                )}{' '}
                                of {processedExpenses.length}
                            </span>
                        </div>
                        <div className="flex gap-1">
                            <button
                                onClick={() => setPage((p) => Math.max(0, p - 1))}
                                disabled={page === 0}
                                className={`p-1 rounded ${page === 0
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'hover:bg-gray-100 dark:hover:bg-neutral-700'
                                    }`}
                            >
                                <svg
                                    className="w-5 h-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>
                            <button
                                onClick={() => setPage((p) => p + 1)}
                                disabled={page >= Math.ceil(processedExpenses.length / rowsPerPage) - 1}
                                className={`p-1 rounded ${page >= Math.ceil(processedExpenses.length / rowsPerPage) - 1
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'hover:bg-gray-100 dark:hover:bg-neutral-700'
                                    }`}
                            >
                                <svg
                                    className="w-5 h-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ExpenseAnalysisTable;
